/**
 * Admin Panel - Redis Cache Inspector API
 *
 * Zod schemas for administrative Redis cache management endpoints.
 * Secure PBAC-protected access to cache inspection and operations.
 *
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import { ROUTE_SENSITIVITY } from "../../route-sensitivity";
import {
  AdminRedisCacheAnalyzeNamespacesResponseSchema,
  AdminRedisCacheDeleteKeyResponseSchema,
  AdminRedisCacheFlushAllResponseSchema,
  AdminRedisCacheFlushQuerySchema,
  AdminRedisCacheGetKeyResponseSchema,
  AdminRedisCacheGetTopConsumersQuerySchema,
  AdminRedisCacheGetTopConsumersResponseSchema,
  AdminRedisCacheIdleAnalysisResponseSchema,
  AdminRedisCacheKeyPathVarsSchema,
  AdminRedisCacheListKeysQuerySchema,
  AdminRedisCacheListKeysResponseSchema,
  AdminRedisCacheMemoryPressureResponseSchema,
  AdminRedisCacheStatsResponseSchema
} from "./schemas/redis-cache.schema";

// ============================================================
// ZAdminRedisCache - Endpoint Definitions
// ============================================================

export const ZAdminRedisCache = {
  /**
   * List cache keys with optional pattern matching
   * GET /api/admin/redis-cache/keys
   *
   * Query Parameters:
   * - pattern: Redis pattern (default: "*")
   * - limit: Max keys to return (default: 100, max: 1000)
   *
   * Returns: List of keys with their types
   */
  ListKeys: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/keys",
    Query: AdminRedisCacheListKeysQuerySchema,
    Response: ApiSuccessSchema(AdminRedisCacheListKeysResponseSchema)
  },

  /**
   * Get key details (value, type, TTL, size)
   * GET /api/admin/redis-cache/keys/:key
   *
   * Path Parameters:
   * - key: Redis key name
   *
   * Returns: Key details including value, type, TTL, and size
   *
   * ⚠️ Withheld from the admin agent (NEX-2903). This is the read-anything case:
   * the response is `value: z.unknown()`, so no schema walk can see what comes
   * back, and the platform demonstrably caches credential material — a plaintext
   * provider API key lives at `api-key:<orgId>` (NEX-2952). `ListKeys` is a read
   * tool too, so the model could enumerate key names and then read that one.
   *
   * Unlike the other SECRET routes this one does not DECLARE a credential field;
   * it can RETURN one by capability. The verdict is the same either way, which is
   * why it reuses the marker rather than adding a second tier that does the same
   * thing. `ListKeys` stays available — it returns names and types, never values.
   *
   * The panel is unaffected: the marker refuses the tool projection only, so an
   * operator still reads cache values on screen.
   */
  GetKey: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/keys/:key",
    sensitivity: ROUTE_SENSITIVITY.SECRET,
    PathVars: AdminRedisCacheKeyPathVarsSchema,
    Response: ApiSuccessSchema(AdminRedisCacheGetKeyResponseSchema)
  },

  /**
   * Delete a specific cache key
   * DELETE /api/admin/redis-cache/keys/:key
   *
   * Path Parameters:
   * - key: Redis key name
   *
   * Returns: Deletion status
   *
   * Security: Requires admin:redis-cache DELETE permission
   * Audit: Logged with key name and deleted by user
   */
  DeleteKey: {
    method: "DELETE" as const,
    path: "/api/admin/redis-cache/keys/:key",
    PathVars: AdminRedisCacheKeyPathVarsSchema,
    Response: ApiSuccessSchema(AdminRedisCacheDeleteKeyResponseSchema)
  },

  /**
   * Flush all cache keys (or matching pattern)
   * POST /api/admin/redis-cache/flush
   *
   * Body Parameters:
   * - pattern: Pattern to flush (default: "*" = all keys)
   * - confirm: Must be "DELETE_ALL_CACHE" for safety
   *
   * Returns: Number of keys deleted
   *
   * Security: Requires admin:redis-cache DELETE permission (ROOT recommended)
   * Audit: Logged with pattern and flushed by user
   * Warning: DANGEROUS - deletes all matching keys
   */
  FlushAll: {
    method: "POST" as const,
    path: "/api/admin/redis-cache/flush",
    Body: AdminRedisCacheFlushQuerySchema,
    Response: ApiSuccessSchema(AdminRedisCacheFlushAllResponseSchema)
  },

  /**
   * Get Redis statistics
   * GET /api/admin/redis-cache/stats
   *
   * Returns: Redis server stats (memory, clients, commands, uptime)
   */
  GetStats: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/stats",
    Response: ApiSuccessSchema(AdminRedisCacheStatsResponseSchema)
  },

  /**
   * Analyze cache by namespace/prefix
   * GET /api/admin/redis-cache/namespaces
   *
   * Groups keys by prefix and returns statistics per namespace:
   * - Key count per namespace
   * - Total size per namespace
   * - Percentage of total cache
   * - Sample keys from each namespace
   *
   * Returns: Array of namespace statistics sorted by size (largest first)
   */
  AnalyzeNamespaces: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/namespaces",
    Response: ApiSuccessSchema(AdminRedisCacheAnalyzeNamespacesResponseSchema)
  },

  /**
   * Get top memory consumers
   * GET /api/admin/redis-cache/top-consumers
   *
   * Returns the largest keys in cache, sorted by memory usage.
   * Helps identify memory optimization opportunities.
   *
   * Query Parameters:
   * - limit: Number of top consumers to return (default: 10, max: 100)
   *
   * Returns: Array of largest keys with their sizes
   */
  GetTopConsumers: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/top-consumers",
    Query: AdminRedisCacheGetTopConsumersQuerySchema,
    Response: ApiSuccessSchema(AdminRedisCacheGetTopConsumersResponseSchema)
  },

  /**
   * Get memory pressure and health status
   * GET /api/admin/redis-cache/memory-pressure
   *
   * Analyzes Redis memory usage, fragmentation, and eviction activity.
   * Provides health status (healthy/warning/critical) and actionable recommendations.
   *
   * Health Thresholds:
   * - < 70%: Healthy - plenty of headroom
   * - 70-85%: Warning - evictions may start soon
   * - > 85%: Critical - active eviction likely
   *
   * Returns: Memory pressure metrics with status and recommendations
   */
  GetMemoryPressure: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/memory-pressure",
    Response: ApiSuccessSchema(AdminRedisCacheMemoryPressureResponseSchema)
  },

  /**
   * Analyze keys by idle time
   * GET /api/admin/redis-cache/idle-analysis
   *
   * Groups keys into buckets based on how long they haven't been accessed.
   * Uses Redis OBJECT IDLETIME (O(1) operation per key).
   *
   * Buckets:
   * - Very Stale (30+ days): Safe to delete
   * - Stale (7-30 days): Consider cleanup
   * - Aging (1-7 days): Monitor
   * - Active (< 1 day): Keep
   *
   * Returns: Bucketed analysis with cleanup recommendations
   */
  AnalyzeIdleKeys: {
    method: "GET" as const,
    path: "/api/admin/redis-cache/idle-analysis",
    Response: ApiSuccessSchema(AdminRedisCacheIdleAnalysisResponseSchema)
  }
} as const;

// ============================================================
// Convenience Type Exports
// ============================================================

export type AdminRedisCacheListKeysQuery = z.infer<typeof AdminRedisCacheListKeysQuerySchema>;
export type AdminRedisCacheListKeysResponse = z.infer<typeof AdminRedisCacheListKeysResponseSchema>;
export type AdminRedisCacheGetKeyResponse = z.infer<typeof AdminRedisCacheGetKeyResponseSchema>;
export type AdminRedisCacheDeleteKeyResponse = z.infer<
  typeof AdminRedisCacheDeleteKeyResponseSchema
>;
export type AdminRedisCacheFlushAllResponse = z.infer<typeof AdminRedisCacheFlushAllResponseSchema>;
export type AdminRedisCacheStatsResponse = z.infer<typeof AdminRedisCacheStatsResponseSchema>;
export type AdminRedisCacheGetTopConsumersQuery = z.infer<
  typeof AdminRedisCacheGetTopConsumersQuerySchema
>;
export type AdminRedisCacheGetTopConsumersResponse = z.infer<
  typeof AdminRedisCacheGetTopConsumersResponseSchema
>;
export type AdminRedisCacheMemoryPressureResponse = z.infer<
  typeof AdminRedisCacheMemoryPressureResponseSchema
>;
export type AdminRedisCacheIdleAnalysisResponse = z.infer<
  typeof AdminRedisCacheIdleAnalysisResponseSchema
>;

// Re-export all schemas for backend use
export * from "./schemas/redis-cache.schema";
